import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { asId } from '@shared/brand';
import { GLOBAL_AREA_SENTINEL } from '@domain/entities/AuditEntry';
import { FEATURE_CATALOG, FEATURES, type FeatureKey } from '@domain/vocabulary';
import { CalendarDate } from '@domain/value-objects/CalendarDate';
import { getServices } from '@infrastructure/services';
import { useAccess } from '../../hooks/useAccess';
import { useRuntimeConfig } from '../../providers/RuntimeConfigProvider';
import { formatDay } from '../../format';

/**
 * The four panels a superuser's Inicio adds.
 *
 * A superuser has no area, no balance and no requests, so none of the personal
 * home applies to them. What they need is whether the platform is configured
 * correctly: which areas exist, which modules are on, and what changed last.
 *
 * Deliberately company-wide rather than area-scoped — this is the one role whose
 * reads legitimately cross areas, and the security rules grant it on the claim.
 */

const LOG_LIMIT = 4;

export interface SystemAreaRow {
  readonly id: string;
  readonly name: string;
  readonly meta: string;
  readonly color: string;
  readonly stateLabel: string;
  readonly active: boolean;
  /** Headcount relative to the largest area, for the bar. */
  readonly percent: number;
}

export interface SystemFeatureRow {
  readonly key: FeatureKey;
  readonly label: string;
  readonly color: string;
  readonly on: boolean;
}

export interface SystemHealthRow {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  /** Drives the dot colour: green when nothing needs attention. */
  readonly ok: boolean;
}

export interface SystemLogRow {
  readonly id: string;
  readonly glyph: string;
  readonly color: string;
  readonly action: string;
  readonly meta: string;
}

export interface SystemHomeSections {
  readonly areas: readonly SystemAreaRow[];
  readonly features: readonly SystemFeatureRow[];
  readonly health: readonly SystemHealthRow[];
  readonly log: readonly SystemLogRow[];
  readonly areaCount: number;
  readonly subAreaCount: number;
  readonly activeFeatureCount: number;
  readonly offFeatureCount: number;
  readonly totalPeople: number;
  readonly tip: string;
}

export const useSystemHomeSections = (enabled: boolean): SystemHomeSections => {
  const config = useRuntimeConfig();
  const { featureOn } = useAccess();
  const today = getServices().clock.today();

  const areas = useQuery({
    queryKey: ['systemHome', 'areas'],
    enabled,
    queryFn: async () => {
      const result = await getServices().organization.listAreas();
      return result.ok ? result.value : [];
    },
  });

  const areaList = useMemo(() => areas.data ?? [], [areas.data]);

  /**
   * Headcount and sub-area count per area.
   *
   * One pair of queries per area rather than a roster read — an area count is a
   * server-side aggregate, and the number of areas is small and bounded by the
   * company, unlike the number of people.
   */
  const perArea = useQuery({
    queryKey: ['systemHome', 'perArea', areaList.map((a) => a.id).join(',')],
    enabled: enabled && areaList.length > 0,
    queryFn: async () => {
      const services = getServices();
      const entries = await Promise.all(
        areaList.map(async (area) => {
          const [people, subs] = await Promise.all([
            services.people.countByStatus(area.id, 'active'),
            services.organization.listSubAreas(area.id),
          ]);
          return {
            areaId: area.id,
            people: people.ok ? people.value : 0,
            subAreas: subs.ok ? subs.value.length : 0,
          };
        }),
      );
      return entries;
    },
  });

  const pendingByArea = useQuery({
    queryKey: ['systemHome', 'pending', areaList.map((a) => a.id).join(',')],
    enabled: enabled && areaList.length > 0,
    queryFn: async () => {
      const services = getServices();
      const counts = await Promise.all(
        areaList.map(async (area) => {
          const result = await services.people.countByStatus(area.id, 'pending');
          return result.ok ? result.value : 0;
        }),
      );
      return counts.reduce((total, n) => total + n, 0);
    },
  });

  const holidays = useQuery({
    queryKey: ['systemHome', 'holidays', today.year],
    enabled,
    queryFn: async () => {
      const result = await getServices().holidays.listForYear(today.year);
      return result.ok ? result.value : [];
    },
  });

  /** The company-wide configuration trail. */
  const log = useQuery({
    queryKey: ['systemHome', 'log'],
    enabled,
    queryFn: async () => {
      const result = await getServices().audit.list(
        { areaId: asId<'AreaId'>(GLOBAL_AREA_SENTINEL) },
        { limit: LOG_LIMIT },
      );
      return result.ok ? result.value.items : [];
    },
  });

  const stats = useMemo(() => {
    const rows = perArea.data ?? [];
    const totalPeople = rows.reduce((n, r) => n + r.people, 0);
    const totalSubAreas = rows.reduce((n, r) => n + r.subAreas, 0);
    const maxPeople = Math.max(1, ...rows.map((r) => r.people));
    return { totalPeople, totalSubAreas, maxPeople, byArea: new Map(rows.map((r) => [r.areaId, r])) };
  }, [perArea.data]);

  const activeFeatureCount = FEATURES.filter((f) => featureOn(f)).length;
  const offFeatureCount = FEATURES.length - activeFeatureCount;
  const areasWithoutAdmin = areaList.filter((a) => a.adminEmail.trim().length === 0);

  const systemAreas = useMemo<SystemAreaRow[]>(
    () =>
      areaList.map((area) => {
        const counts = stats.byArea.get(area.id);
        const people = counts?.people ?? 0;
        return {
          id: area.id,
          name: area.name,
          meta: `${String(counts?.subAreas ?? 0)} sub áreas · ${String(people)} personas`,
          color: area.color,
          stateLabel: area.active ? 'Activa' : 'Inactiva',
          active: area.active,
          percent: Math.round((people / stats.maxPeople) * 100),
        };
      }),
    [areaList, stats],
  );

  const systemFeatures = useMemo<SystemFeatureRow[]>(
    () =>
      FEATURE_CATALOG.map((descriptor) => ({
        key: descriptor.key,
        label: descriptor.label,
        color: descriptor.color,
        // `featureOn` already resolves the dependency, so a module whose parent
        // is off reads as inactive rather than as on-but-unreachable.
        on: featureOn(descriptor.key),
      })),
    [featureOn],
  );

  const activeHolidays = (holidays.data ?? []).filter((h) => h.enabled).length;
  const totalHolidays = (holidays.data ?? []).length;
  const pendingAccounts = pendingByArea.data ?? 0;

  const systemHealth = useMemo<SystemHealthRow[]>(
    () => [
      {
        id: 'cuentas',
        label: 'Cuentas totales en la plataforma',
        value: String(stats.totalPeople),
        ok: true,
      },
      {
        id: 'pendientes',
        label: 'Cuentas pendientes de validación',
        value: String(pendingAccounts),
        ok: pendingAccounts === 0,
      },
      {
        id: 'dominio',
        label: 'Dominio de acceso permitido',
        value: config.identity.allowedDomains.join(', '),
        ok: config.identity.allowedDomains.length > 0,
      },
      {
        id: 'feriados',
        label: 'Feriados cargados',
        value: `${String(activeHolidays)} / ${String(totalHolidays)}`,
        ok: featureOn('feriados'),
      },
      {
        id: 'sin-admin',
        label: 'Áreas sin administrador',
        value: String(areasWithoutAdmin.length),
        ok: areasWithoutAdmin.length === 0,
      },
      {
        id: 'modulos',
        label: 'Módulos apagados',
        value: String(offFeatureCount),
        ok: offFeatureCount === 0,
      },
    ],
    [
      stats.totalPeople,
      pendingAccounts,
      config.identity.allowedDomains,
      activeHolidays,
      totalHolidays,
      featureOn,
      areasWithoutAdmin.length,
      offFeatureCount,
    ],
  );

  const systemLog = useMemo<SystemLogRow[]>(
    () =>
      (log.data ?? []).map((entry) => ({
        id: entry.id,
        glyph: entry.kind === 'Política' ? '§' : entry.kind === 'Cuenta' ? '@' : '▤',
        color:
          entry.areaId === GLOBAL_AREA_SENTINEL
            ? 'var(--color-violet)'
            : entry.kind === 'Cuenta'
              ? 'var(--color-danger-bright)'
              : 'var(--color-brand)',
        action: entry.action,
        meta: `${entry.areaName} · ${entry.actorName} · ${formatDay(
          CalendarDate.fromInstantLocal(entry.createdAt),
        )}`,
      })),
    [log.data],
  );

  /**
   * The recommendation, in the prototype's order of urgency: an area with no
   * administrator blocks its own onboarding, so it outranks a module being off.
   */
  const tip = useMemo(() => {
    if (areasWithoutAdmin.length > 0) {
      return `Hay ${String(areasWithoutAdmin.length)} ${
        areasWithoutAdmin.length === 1 ? 'área' : 'áreas'
      } sin administrador asignado: nadie podrá crear sus sub áreas ni validar cuentas hasta que designes uno.`;
    }
    if (offFeatureCount > 0) {
      return `${String(offFeatureCount)} ${
        offFeatureCount === 1 ? 'módulo está apagado' : 'módulos están apagados'
      } para todas las áreas. Sus registros quedan en resguardo y se recuperan al reactivarlos.`;
    }
    return 'Todo activo y con administrador asignado. Al apagar un módulo se oculta de la navegación de todos los perfiles sin borrar datos.';
  }, [areasWithoutAdmin.length, offFeatureCount]);

  return {
    areas: systemAreas,
    features: systemFeatures,
    health: systemHealth,
    log: systemLog,
    areaCount: areaList.length,
    subAreaCount: stats.totalSubAreas,
    activeFeatureCount,
    offFeatureCount,
    totalPeople: stats.totalPeople,
    tip,
  };
};
